import { GetTopDestinationSvcType } from "@/actions/destination.action";
import dayjs from "dayjs";
import { DateRange } from "react-day-picker";
import {
  IconBike,
  IconMotorbike,
  IconCompass,
  IconBuildingMonument,
  IconMapPin,
  IconPalette,
  IconTrees,
  IconSparkles,
  IconMasksTheater,
  IconHistory,
  IconToolsKitchen2,
  IconCampfire,
} from "@tabler/icons-react";

export type SearchServiceKey =
  "hotels" | "rentals" | "spa_msg" | "tour_act" | "discover";

export type QueryField =
  | "locationId"
  | "search"
  | "lat"
  | "lng"
  | "start"
  | "end"
  | "date"
  | "adults"
  | "children"
  | "rooms"
  | "vehicleType"
  | "pickupType";

export const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

export const toISODate = (value?: Date | string | null): string =>
  value ? dayjs(value).format("YYYY-MM-DD") : "";

export const SERVICE_KEY_MAP: Record<string, SearchServiceKey> = {
  hotels: "hotels",
  hotel: "hotels",
  rentals: "rentals",
  rental: "rentals",
  spa_msg: "spa_msg",
  tour_act: "tour_act",
  discover: "discover",
};

export const toSearchServiceKey = (
  service?: string | null,
): SearchServiceKey => {
  if (!service) return "hotels";
  const normalized = service.toLowerCase().trim().replace(/-/g, "_");
  return (
    SERVICE_KEY_MAP[normalized] ??
    (normalized.includes("hotel")
      ? "hotels"
      : normalized.includes("rental")
        ? "rentals"
        : normalized.includes("spa")
          ? "spa_msg"
          : normalized.includes("tour") || normalized.includes("activit")
            ? "tour_act"
            : normalized.includes("discover")
              ? "discover"
              : "hotels")
  );
};

export const SERVICE_NAME_TO_SVC_TYPE: Record<
  string,
  GetTopDestinationSvcType
> = {
  hotels: "hotel",
  hotel: "hotel",
  rentals: "rental",
  rental: "rental",
  tour_act: "tour_act",
  spa_msg: "spa_msg",
};

export const DEFAULT_SVC_TYPE: GetTopDestinationSvcType = "hotel";

export const travelerFieldConfig = [
  { key: "adults", label: "Adults", minimum: 1 },
  { key: "children", label: "Children", minimum: 0 },
  { key: "rooms", label: "Rooms", minimum: 1 },
] as const;

export type SearchModuleFormValues = {
  locationId: string;
  search: string;
  lat?: number;
  lng?: number;
  rangeValue?: DateRange;
  singleDate?: Date;
  adults: number;
  children: number;
  rooms: number;
  vehicleType: string;
  pickupType: string;
};

// rental

export const DEFAULT_VEHICLE_TYPE = "motorbike";
export const DEFAULT_PICKUP_TIME = "1";

export const vehicleTypeOptions = [
  { value: "motorbike", label: "Motorcycle", icon: IconMotorbike },
  { value: "bike", label: "Bicycle", icon: IconBike },
];

export const pickUpTimeOptions = [
  { value: "1", label: "06:00 AM - 12:00 PM" },
  { value: "2", label: "12:00 PM - 09:00 PM" },
];

// discover

export const exploreDestinationOptions = [
  { value: "all", label: "All Destinations", icon: IconCompass },
  {
    value: "siem_reap",
    label: "Siem Reap & Angkor",
    icon: IconBuildingMonument,
  },
  { value: "phnom_penh", label: "Phnom Penh", icon: IconMapPin },
  { value: "battambang", label: "Battambang", icon: IconPalette },
  { value: "kampot_kep", label: "Kampot & Kep", icon: IconTrees },
  { value: "coastal", label: "Koh Rong & Islands", icon: IconMapPin },
  { value: "ecotourism", label: "Mondulkiri & Highlands", icon: IconTrees },
];

export const discoverCategoryOptions = [
  { value: "all", label: "All Categories", icon: IconSparkles },
  { value: "culture", label: "Culture", icon: IconMasksTheater },
  { value: "art", label: "Art", icon: IconPalette },
  { value: "traditions", label: "Traditions", icon: IconBuildingMonument },
  { value: "history", label: "History & Heritage", icon: IconHistory },
  { value: "nature", label: "Nature & Wildlife", icon: IconTrees },
  { value: "food", label: "Food & Culinary", icon: IconToolsKitchen2 },
  { value: "adventure", label: "Adventure", icon: IconCampfire },
  { value: "festivals", label: "Festivals & Events", icon: IconSparkles },
];

/*************************************
 * config - service capability
 *********************************** */
export type DateFieldMode = "range" | "single" | "none";
export type ExtraFieldType = "travelers" | "rental" | "discover" | null;

export type ServiceFormCapabilities = {
  hasLocation: boolean;
  dateMode: DateFieldMode;
  extraField: ExtraFieldType;
  isHotelSync: boolean;
};

/**
 * Service capability configuration tuple:
 * [hasLocation, dateMode, extraField, isHotelSync]
 */
export type ServiceCapabilityTuple = [
  hasLocation: boolean,
  dateMode: DateFieldMode,
  extraField: ExtraFieldType,
  isHotelSync: boolean,
];

export const SERVICE_FORM_TUPLE_MAP: Record<
  SearchServiceKey,
  ServiceCapabilityTuple
> = {
  hotels: [true, "range", "travelers", true],
  rentals: [true, "single", "rental", false],
  spa_msg: [true, "single", null, false],
  tour_act: [true, "single", null, false],
  discover: [false, "none", "discover", false],
};

export const getServiceCapabilities = (
  serviceKey: SearchServiceKey,
): ServiceFormCapabilities => {
  const [hasLocation, dateMode, extraField, isHotelSync] =
    SERVICE_FORM_TUPLE_MAP[serviceKey] ?? SERVICE_FORM_TUPLE_MAP.hotels;

  return {
    hasLocation,
    dateMode,
    extraField,
    isHotelSync,
  };
};

/**
 * Service-specific form comparison tuples
 */
export type HotelFormTuple = [
  locationId: string,
  search: string,
  lat: number | undefined,
  lng: number | undefined,
  start: string,
  end: string,
  adults: number,
  children: number,
  rooms: number,
];

export type RentalFormTuple = [
  locationId: string,
  lat: number | undefined,
  lng: number | undefined,
  date: string,
  vehicleType: string,
  pickupType: string,
];

export type SingleDateFormTuple = [
  locationId: string,
  lat: number | undefined,
  lng: number | undefined,
  date: string,
];

/**
 * Compares two tuples element-by-element for clean change detection
 */
export const hasTupleChanged = <T extends readonly unknown[]>(
  formTuple: T,
  queryTuple: T,
): boolean => formTuple.some((val, i) => val !== queryTuple[i]);
